import { fmtCislo } from './receptUtils';

/**
 * Kalkulační koláč — celá koruna, kterou zákazník zaplatí, v jednom kruhu.
 *
 * Dřív tu byl donut jen z nákladů a marže se dopočítávala vedle ve třech číslech.
 * Z toho ale nešlo přečíst to hlavní: kolik na položce vyděláme. Kruh proto
 * odpovídá **prodejní ceně s DPH** a dělí se na tři druhy podílu:
 *   náklady (materiál, odpad, stroje, práce, balení) — tenké, je jich hodně
 *   marže — tlustý oblouk, vystupuje z prstence ven i dovnitř
 *   DPH — tenké a šedé, není naše
 *
 * Rozdílná tloušťka dělá práci, kterou by jinak musela dělat legenda: marže je
 * vidět dřív, než člověk stihne číst. Začíná se v 12 hodin, aby byla první.
 *
 * Ztrátová položka (marže < 0) se nedá nakreslit jako výseč — kruh se pak počítá
 * z nákladů + DPH a schodek jde do středu červeně. Bez ceny (polotovar) zůstane
 * kruh čistě nákladový.
 *
 * Kreslí se do viewBoxu a text je uvnitř SVG, takže se celé škáluje s šířkou
 * kontejneru — od úzkého sloupce po plnou šířku na mobilu.
 */

// Náklady dostávají studenější odstíny, marže zelenou (peníze), DPH šedou.
// Balení bylo dřív zelené — o tu barvu se teď hlásí marže, aby vystupovala.
export const KOLAC_BARVY = {
    material: '#06B6D4',
    odpad:    '#EF4444',
    stroje:   '#F97316',
    prace:    '#8B5CF6',
    baleni:   '#EAB308',
    marze:    '#10B981',
    dph:      '#64748B',
    ztrata:   '#EF4444',
};

const VB = 220;             // viewBox — čtverec, geometrie v jeho jednotkách
const R = 82;               // poloměr střednice prstence
const SW_TENKY = 13;        // náklady a DPH
const SW_TLUSTY = 27;       // marže — hero
const MEZERA = 3.4;         // odstup mezi výsečemi (v délce oblouku)

/**
 * Sestaví výseče koláče z kalkulace a kanálu.
 * @returns {{polozky: Array, zaklad: number, marze: number|null, ztrata: boolean, maCenu: boolean}}
 */
export function sestavKolac(kalkulace, nakladBaleni, vKanalu) {
    const naklady = [
        { id: 'material', label: 'Materiál', value: kalkulace?.material_cost ?? 0 },
        { id: 'odpad',    label: 'Odpad',    value: kalkulace?.scrap_cost ?? 0 },
        { id: 'stroje',   label: 'Stroje',   value: kalkulace?.machine_cost ?? 0 },
        { id: 'prace',    label: 'Práce',    value: kalkulace?.labor_cost ?? 0 },
        { id: 'baleni',   label: 'Balení',   value: nakladBaleni ?? 0 },
    ].map(p => ({ ...p, color: KOLAC_BARVY[p.id], tlusty: false }));

    const nakladCelkem = naklady.reduce((s, p) => s + p.value, 0);
    const maCenu = (vKanalu?.prodejni_cena ?? 0) > 0;

    if (!maCenu) {
        // Polotovar nebo položka bez ceny — kruh je čistě nákladový.
        return { polozky: naklady, zaklad: nakladCelkem, marze: null, ztrata: false, maCenu: false };
    }

    const dph = vKanalu.dph ?? 0;
    const marze = vKanalu.marze ?? 0;
    const ztrata = marze < 0;

    // Marže první (12 hodin), pak náklady, DPH kruh uzavírá.
    const polozky = [
        ...(ztrata ? [] : [{ id: 'marze', label: 'Marže', value: marze, color: KOLAC_BARVY.marze, tlusty: true }]),
        ...naklady,
        ...(dph > 0 ? [{ id: 'dph', label: 'DPH', value: dph, color: KOLAC_BARVY.dph, tlusty: false }] : []),
    ];

    // Při ztrátě cena náklady nepokryje — základ je to, co položka reálně stojí.
    const zaklad = ztrata ? nakladCelkem + dph : (vKanalu.cena_s_dph ?? nakladCelkem + dph);

    return { polozky, zaklad, marze, ztrata, maCenu: true };
}

export default function KalkulacniKolac({
    polozky, zaklad, marze, ztrata, maCenu, jednotka = 'ks', mutedColor, maxSirka = 240, jenCislo = false,
}) {
    const cx = VB / 2;
    const obvod = 2 * Math.PI * R;
    const delitel = Math.max(zaklad, 0.0001);

    // Výseče se skládají po obvodu; nulové se přeskočí, ať nedělají mezery.
    let posun = 0;
    const vysece = polozky
        .filter(p => p.value > 0.005)
        .map(p => {
            const podil = p.value / delitel;
            const delka = Math.max(podil * obvod - MEZERA, 1.2);
            const v = { ...p, delka, posun };
            posun += podil * obvod;
            return v;
        });

    // Střed: co si necháme. Bez ceny náklad, při ztrátě schodek červeně.
    const stred = !maCenu
        ? { cislo: fmtCislo(zaklad), popis: `Kč / ${jednotka}`, spodek: 'náklad', barva: 'var(--text-primary)' }
        : ztrata
            ? { cislo: fmtCislo(marze), popis: 'Kč', spodek: 'ztráta na kus', barva: KOLAC_BARVY.ztrata }
            : { cislo: fmtCislo(marze), popis: 'Kč', spodek: `marže / ${jednotka}`, barva: KOLAC_BARVY.marze };

    // Do vnitřního kruhu se vejde ~137 jednotek. Čtyřmístná částka („1 234,56")
    // by na 30 přetekla přes prstenec, tak se u delších čísel písmo zmenší.
    const velikostCisla = stred.cislo.length <= 6 ? 30 : stred.cislo.length === 7 ? 26 : 21;
    // Malý koláč (nízká karta): popisky pod číslem by byly pár pixelů vysoké —
    // zůstane jen číslo, zato větší, ať se dá přečíst. Význam nese legenda.
    const velikostSamotneho = stred.cislo.length <= 5 ? 46 : stred.cislo.length === 6 ? 40 : stred.cislo.length === 7 ? 34 : 28;

    return (
        <svg viewBox={`0 0 ${VB} ${VB}`} role="img"
            aria-label={maCenu ? `Marže ${fmtCislo(marze)} Kč z ceny ${fmtCislo(zaklad)} Kč s DPH` : `Náklad ${fmtCislo(zaklad)} Kč`}
            style={{ width: '100%', maxWidth: maxSirka, height: 'auto', display: 'block', margin: '0 auto' }}>
            <g transform={`rotate(-90 ${cx} ${cx})`}>
                {vysece.map(v => (
                    <circle key={v.id} cx={cx} cy={cx} r={R} fill="none"
                        stroke={v.color} strokeWidth={v.tlusty ? SW_TLUSTY : SW_TENKY} strokeLinecap="butt"
                        strokeDasharray={`${v.delka} ${obvod - v.delka}`} strokeDashoffset={-v.posun}
                        style={{ transition: 'stroke-dasharray 0.4s, stroke-dashoffset 0.4s' }}>
                        <title>{`${v.label}: ${fmtCislo(v.value)} Kč (${((v.value / delitel) * 100).toFixed(0)} %)`}</title>
                    </circle>
                ))}
            </g>

            {/* Text uvnitř SVG — škáluje se s kruhem, nemusí se dopočítávat v CSS */}
            {jenCislo ? (
                <text x={cx} y={cx} textAnchor="middle" dominantBaseline="central" fontSize={velikostSamotneho} fontWeight="700" style={{ fill: stred.barva }}>
                    {stred.cislo}
                </text>
            ) : (
                <>
                    <text x={cx} y={cx - 6} textAnchor="middle" fontSize={velikostCisla} fontWeight="700" style={{ fill: stred.barva }}>
                        {stred.cislo}
                    </text>
                    <text x={cx} y={cx + 12} textAnchor="middle" fontSize="12" style={{ fill: mutedColor }}>
                        {stred.popis}
                    </text>
                    <text x={cx} y={cx + 29} textAnchor="middle" fontSize="10.5" opacity="0.85" style={{ fill: mutedColor }}>
                        {stred.spodek}
                    </text>
                </>
            )}
        </svg>
    );
}
